import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './api/client';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useSearchParams,
  useParams,
} from 'react-router-dom';
import HomeScreen from './screens/dashboard/HomeScreen';
import OnboardingScreen from './screens/onboarding/OnboardingScreen';
import SplashScreen from './components/auth/SplashScreen';
import AuthErrorScreen from './components/auth/AuthErrorScreen';
import { InvitationScreen } from './screens/invitation/InvitationScreen';
import CallScreen from './screens/calls/CallScreen';
import InspectionsScreen from './screens/inspections/InspectionsScreen';
import InspectionRunScreen from './screens/inspections/InspectionRunScreen';
import InspectionReportScreen from './screens/inspections/InspectionReportScreen';
import { DriverProfile, DriverCall } from './services/driverApi';
import { useHybridAuth } from './hooks/useHybridAuth';
import { ToastProvider } from './contexts/ToastContext';
import { isEmbeddedMode } from './utils/embeddedMode';
import { takeReturnTo } from './lib/returnTo';
import { AuthErrorKind } from './utils/authErrorKind';
import { oneSignalService } from './services/oneSignal';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { UpdateGate } from './mobile/UpdateGate';
import { MobileApp } from './mobile/MobileApp';
import { LandingScreen } from './mobile/auth/LandingScreen';
import { SignUpScreen } from './mobile/auth/SignUpScreen';
import { useNewShell } from './hooks/useNewShell';
import { MobileTheme } from '@relay-towing/ui';

// Helper component to wrap CallScreen with route parameters
const CallScreenWrapper: React.FC = () => {
  const { callId } = useParams<{ callId: string }>();
  const navigate = useNavigate();

  if (!callId) {
    return <Navigate to="/" replace />;
  }

  return (
    <CallScreen
      callId={callId}
      onBack={() => navigate('/')}
      showHeader={true}
    />
  );
};

// Helper component to handle early embedded mode detection
const EmbeddedModeHandler: React.FC = () => {
  useEffect(() => {
    // Check for embedded mode parameter and store it immediately
    // This runs as soon as the app loads, before authentication
    const isEmbedded = isEmbeddedMode();
    console.log('🔗 [App] Early embedded mode detection:', isEmbedded);
  }, []);

  return null;
};

// Helper component to handle token-based navigation
const TokenHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (token) {
      console.log(
        '[TokenHandler] Found token in URL, navigating to invitation page'
      );
      // Store the token for the invitation screen
      localStorage.setItem('pending_invitation_token', token);
      // Navigate to invitation page
      navigate('/invitation', { replace: true });
    }
  }, [location.search, navigate]);

  return null;
};

// Helper component to handle OneSignal registration after authentication
const OneSignalHandler: React.FC = () => {
  const auth = useHybridAuth();
  const { isAuthenticated, isLoading } = auth;

  useEffect(() => {
    // Only register OneSignal after authentication is complete
    if (!isLoading && isAuthenticated) {
      console.log(
        '[OneSignalHandler] User authenticated - registering OneSignal player ID'
      );
      oneSignalService.registerAfterAuthentication();
    }
  }, [isAuthenticated, isLoading]);

  return null;
};

// Main App Content component that handles the different states
const AppContent: React.FC = () => {
  const [showSplash, setShowSplash] = useState(true);
  const [dataPreloaded, setDataPreloaded] = useState(false);
  const [preloadError, setPreloadError] = useState<string | null>(null);
  const [preloadedData, setPreloadedData] = useState<{
    profile: DriverProfile;
    calls: DriverCall[];
  } | null>(null);
  const [authError, setAuthError] = useState<AuthErrorKind | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useHybridAuth();
  const { isAuthenticated, isLoading } = auth;

  // Use persistent embedded mode detection (handles URL params and localStorage)
  const isEmbedded = isEmbeddedMode();
  const newShell = useNewShell();

  // Auth0 sends the driver back to the origin, not to the page they opened.
  // A link to one checklist would land on the home screen without this.
  //
  // Once. A driver who signs in, walks to the home screen and comes back must
  // not be thrown at the old link again.
  const returnedTo = useRef(false);
  useEffect(() => {
    if (returnedTo.current || isLoading || !isAuthenticated) return;
    returnedTo.current = true;
    const path = takeReturnTo();
    if (path) navigate(path, { replace: true });
  }, [isAuthenticated, isLoading, navigate]);

  const handleSplashComplete = (
    dataLoaded: boolean,
    data?: { profile: DriverProfile; calls: DriverCall[] },
    error?: string,
    authErrorKind?: AuthErrorKind | null
  ) => {
    setDataPreloaded(dataLoaded);
    setPreloadError(error || null);
    setPreloadedData(data || null);
    setAuthError(authErrorKind ?? null);
    setShowSplash(false);
  };

  // The splash sets authError once and nothing else re-runs its load, so a
  // 401 that was only a token refresh in flight would stick for the whole
  // mount. Mounting the splash again runs the load from the top.
  const handleAuthRetry = () => {
    setAuthError(null);
    setPreloadError(null);
    setShowSplash(true);
  };

  return (
    <Routes>
      {/* Invitation route - available in all states */}
      <Route path="/invitation" element={<InvitationScreen />} />

      {/* Show splash screen first */}
      {showSplash && (
        <Route
          path="*"
          element={<SplashScreen onComplete={handleSplashComplete} />}
        />
      )}

      {/* Show loading state while Auth0 is checking authentication */}
      {!showSplash && isLoading && (
        <Route
          path="*"
          element={
            <div className="fixed inset-0 bg-primary flex items-center justify-center">
              <div className="text-white text-lg">Loading...</div>
            </div>
          }
        />
      )}

      {/* Signed out: the landing page, and the phone's own sign-up */}
      {!showSplash && !isLoading && !isAuthenticated && (
        <>
          <Route
            path="/signup"
            element={
              <MobileTheme>
                <SignUpScreen />
              </MobileTheme>
            }
          />
          <Route
            path="*"
            element={
              <MobileTheme>
                <LandingScreen />
              </MobileTheme>
            }
          />
        </>
      )}

      {/* The splash already knows the session is dead, so say so here rather
          than letting an empty home screen flash first. /invitation is declared
          above and outside this branch, so it stays reachable. */}
      {!showSplash && !isLoading && isAuthenticated && authError !== null && (
        <Route
          path="*"
          element={
            <AuthErrorScreen
              kind={authError}
              onRetry={handleAuthRetry}
              errorMessage={preloadError ?? undefined}
            />
          }
        />
      )}

      {/* Show main app if authenticated */}
      {!showSplash && !isLoading && isAuthenticated && authError === null && (
        <>
          {newShell ? (
            <Route path="*" element={<MobileApp />} />
          ) : (
            <>
              {/* The old shell's onboarding; the new shell has its own
                  setup checklist at this path. */}
              <Route
                path="/onboarding"
                element={
                  <QueryClientProvider client={queryClient}>
                    <div className="App bg-primary min-h-screen">
                      <OnboardingScreen />
                    </div>
                  </QueryClientProvider>
                }
              />
              <Route
                path="/"
                element={
                  <QueryClientProvider client={queryClient}>
                    <div className="App bg-primary min-h-screen">
                      <HomeScreen
                        dataPreloaded={dataPreloaded}
                        preloadError={preloadError}
                        preloadedData={preloadedData}
                        showHeader={true}
                        showMenu={!isEmbedded}
                        showSettings={true}
                        dataLoadError={!dataPreloaded && preloadError !== null}
                      />
                    </div>
                  </QueryClientProvider>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <QueryClientProvider client={queryClient}>
                    <div className="App bg-primary min-h-screen">
                      <HomeScreen
                        dataPreloaded={dataPreloaded}
                        preloadError={preloadError}
                        preloadedData={preloadedData}
                        showHeader={true}
                        showMenu={!isEmbedded}
                        showSettings={true}
                        dataLoadError={!dataPreloaded && preloadError !== null}
                      />
                    </div>
                  </QueryClientProvider>
                }
              />
              <Route
                path="/calls/:callId"
                element={
                  <QueryClientProvider client={queryClient}>
                    <div className="App bg-primary min-h-screen">
                      <CallScreenWrapper />
                    </div>
                  </QueryClientProvider>
                }
              />
              <Route
                path="/inspections"
                element={
                  <QueryClientProvider client={queryClient}>
                    <div className="App bg-primary min-h-screen">
                      <InspectionsScreen />
                    </div>
                  </QueryClientProvider>
                }
              />
              <Route
                path="/inspections/run/:templateId"
                element={
                  <QueryClientProvider client={queryClient}>
                    <div className="App bg-primary min-h-screen">
                      <InspectionRunScreen />
                    </div>
                  </QueryClientProvider>
                }
              />
              <Route
                path="/inspections/report/:id"
                element={
                  <QueryClientProvider client={queryClient}>
                    <div className="App bg-primary min-h-screen">
                      <InspectionReportScreen />
                    </div>
                  </QueryClientProvider>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </>
      )}
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <ToastProvider>
        <EmbeddedModeHandler />
        <TokenHandler />
        <OneSignalHandler />
        <ErrorBoundary>
          {/* Above sign-in: a wrapper build too old for the server is told
              before it tries to sign in, which it may no longer manage. */}
          <QueryClientProvider client={queryClient}>
            <UpdateGate>
              <AppContent />
            </UpdateGate>
          </QueryClientProvider>
        </ErrorBoundary>
      </ToastProvider>
    </Router>
  );
}

export default App;
